import { useState, useCallback, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '@/lib/store/authStore'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Checkbox } from '@/components/ui/checkbox'
import { Textarea } from '@/components/ui/textarea'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  ArrowLeft,
  Clock,
  ShoppingCart,
  CheckCircle2,
  Users,
  Loader2,
  Send,
  AlertCircle,
  Link2,
  Type,
  Sparkles,
  Film,
  Play,
  Ticket,
  ChevronDown,
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { promotionApi } from '@/features/promotions/api/promotionApi'
import { LineOAStoreSwitcher } from './LineOAStoreSwitcher'
import {
  RichEditor,
  VideoEditor,
  type RichState,
  type VideoState,
} from './BroadcastRichVideoEditor'
import { useLineMessagingConfig } from '@/features/storefront/settings/useLineMessagingConfig'
import { useLineCampaign, useLineCampaignFilterOptions } from './useLineCampaign'
import { CampaignSentHistory } from './CampaignSentHistory'
import { AudienceFilterBuilder } from './AudienceFilterBuilder'
import { usePageTranslation } from '@/i18n/useTranslation'
import { lineCampaignTranslations as enT } from '@/i18n/locales/en/lineCampaign'
import { lineCampaignTranslations as thT } from '@/i18n/locales/th/lineCampaign'
import type {
  LineCampaignSegment,
  LineCampaignRecipient,
  LineCampaignFilterCondition,
} from '@/lib/api/controlPlaneClient'

// Labels/descriptions come from i18n (seg_<key> / seg_<key>_desc) — keep only the icon here.
const SEGMENTS: { key: LineCampaignSegment; icon: typeof Clock }[] = [
  { key: 'awaiting_payment', icon: Clock },
  { key: 'abandoned_cart', icon: ShoppingCart },
  { key: 'paid', icon: CheckCircle2 },
  { key: 'all_customers', icon: Users },
]

const MAX_TEXT = 4500

function baht(n: number) {
  return `฿${(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`
}

type CampaignMessageType = 'text' | 'rich' | 'video'

// Live LINE chat-bubble preview of the message (text / rich / video).
function MessagePreview({
  mode,
  text,
  withLink,
  placeholder,
  rich,
  video,
}: {
  mode: CampaignMessageType
  text: string
  withLink: boolean
  placeholder: string
  rich?: { imageUrl: string | null; title: string; label: string }
  video?: { posterUrl: string | null }
}) {
  return (
    <div className="bg-gray-100 rounded-lg p-3">
      {mode === 'rich' ? (
        rich?.imageUrl ? (
          <div className="max-w-[280px] bg-white rounded-xl shadow-sm overflow-hidden">
            <img src={rich.imageUrl} alt="" className="w-full object-cover" />
            <div className="p-2.5 space-y-2">
              {rich.title.trim() && (
                <p className="text-[13px] font-bold text-[#111] leading-snug">{rich.title}</p>
              )}
              <div className="rounded-lg bg-[#06C755] text-white text-[12px] font-medium text-center py-1.5">
                {rich.label.trim() || 'View'}
              </div>
            </div>
          </div>
        ) : (
          <div className="max-w-[280px] rounded-xl bg-white shadow-sm p-3 text-[13px] text-gray-400">{placeholder}</div>
        )
      ) : mode === 'video' ? (
        video?.posterUrl ? (
          <div className="relative max-w-[280px] rounded-xl overflow-hidden shadow-sm">
            <img src={video.posterUrl} alt="" className="w-full object-cover" />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="w-10 h-10 rounded-full bg-black/50 flex items-center justify-center">
                <Play className="w-5 h-5 text-white ml-0.5" />
              </span>
            </div>
          </div>
        ) : (
          <div className="max-w-[280px] rounded-xl bg-white shadow-sm p-3 text-[13px] text-gray-400">{placeholder}</div>
        )
      ) : (
        <div className="max-w-[280px] bg-white rounded-xl shadow-sm overflow-hidden text-[13px] leading-[1.5] p-3 whitespace-pre-wrap break-words">
          {text.trim() ? text : <span className="text-gray-400">{placeholder}</span>}
          {withLink && (
            <div className="mt-2 text-blue-600 underline break-all">
              https://your-store.shop.zaviago.com/order/SO-XXXX?token=…
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export function LineCampaignPage() {
  const t = usePageTranslation({ en: enT, th: thT })
  const { status, isLoading: statusLoading } = useLineMessagingConfig()
  const {
    previewAsync,
    previewData,
    isPreviewing,
    previewError,
    resetPreview,
    send,
    sendResult,
    isSending,
    sendError,
    resetSend,
  } = useLineCampaign()

  const [segment, setSegment] = useState<LineCampaignSegment | null>(null)
  const [minOrders, setMinOrders] = useState('')
  const [minSpend, setMinSpend] = useState('')
  const [conditions, setConditions] = useState<LineCampaignFilterCondition[]>([])
  const { data: filterOptions, isLoading: filterOptionsLoading } =
    useLineCampaignFilterOptions(status?.connected ?? false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [message, setMessage] = useState('')
  const [includeLink, setIncludeLink] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)

  // Message type (text / rich Flex / video) + their authoring state. Rich/video
  // reuse the SAME editors as the broadcast compose page (BroadcastRichVideoEditor).
  const [msgType, setMsgType] = useState<CampaignMessageType>('text')
  const [rich, setRichState] = useState<RichState>({
    imageFile: null, imageUrl: null, link: '', label: '', title: '',
  })
  const [video, setVideoState] = useState<VideoState>({
    videoFile: null, posterFile: null, posterUrl: null,
  })
  const setRich = useCallback((patch: Partial<RichState>) => setRichState((s) => ({ ...s, ...patch })), [])
  const setVideo = useCallback((patch: Partial<VideoState>) => setVideoState((s) => ({ ...s, ...patch })), [])

  // Coupon picked for THIS send — stored on the broadcast log so the Sent-detail
  // page attributes real orders to it exactly (no message-text guessing). Optional.
  const [couponCode, setCouponCode] = useState('')
  const currentTenant = useAuthStore((s) => s.currentTenant)
  const { data: couponData } = useQuery({
    queryKey: ['line-campaign-coupons', currentTenant],
    queryFn: () => promotionApi.list(0, 100),
    staleTime: 60_000,
    enabled: !!currentTenant && (status?.connected ?? false),
  })
  const coupons = couponData?.promotions ?? []

  // Switching store changes which OA/customers we target — clear the stale
  // recipient preview so it can't mislead (the send also re-validates server-side).
  const activeStorefrontKey = useAuthStore((s) => s.activeStorefrontKey)
  useEffect(() => {
    resetPreview()
    resetSend()
    setSelected(new Set())
    setConditions([]) // products/collections differ per store
    setMsgType('text')
    setRichState({ imageFile: null, imageUrl: null, link: '', label: '', title: '' })
    setVideoState({ videoFile: null, posterFile: null, posterUrl: null })
    setCouponCode('') // coupons are per-store — clear the stale pick
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStorefrontKey])

  const recipients: LineCampaignRecipient[] = previewData?.recipients ?? []
  // Segments where the row is about the CART, so lifetime "0 orders" reads as a
  // phantom zero rather than a signal — we clarify those with a new-customer chip.
  const cartSegment = segment === 'abandoned_cart' || segment === 'awaiting_payment'

  const handleSelectSegment = useCallback(
    (key: LineCampaignSegment) => {
      setSegment(key)
      setIncludeLink(key === 'awaiting_payment')
      resetPreview()
      resetSend()
      setSelected(new Set())
    },
    [resetPreview, resetSend]
  )

  const handlePreview = useCallback(async () => {
    if (!segment) return
    resetSend()
    const res = await previewAsync({
      segment,
      minOrders: parseInt(minOrders || '0', 10) || 0,
      minSpend: parseFloat(minSpend || '0') || 0,
      filters: conditions,
    })
    // Default: select everyone reachable
    setSelected(new Set(res.recipients.map((r) => r.line_user_id)))
  }, [segment, minOrders, minSpend, conditions, previewAsync, resetSend])

  const allSelected = recipients.length > 0 && selected.size === recipients.length
  const toggleAll = useCallback(() => {
    setSelected(
      allSelected ? new Set() : new Set(recipients.map((r) => r.line_user_id))
    )
  }, [allSelected, recipients])

  const toggleOne = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const richValid = !!rich.imageFile && /^https:\/\//.test(rich.link.trim())
  const videoValid = !!video.videoFile && !!video.posterFile
  const composeValid =
    msgType === 'rich' ? richValid : msgType === 'video' ? videoValid : message.trim().length > 0
  const canSend = selected.size > 0 && composeValid && !isSending

  // Honest outcome: a result is a SUCCESS only when at least one message reached
  // LINE (sent_count > 0). When nothing sent, explain WHY in the user's language.
  const sendSucceeded = !!sendResult && sendResult.success && sendResult.sent_count > 0
  const sendFailureMsg =
    sendResult && !sendSucceeded
      ? sendResult.line_error
        ? t.lineRejected.replace('{reason}', sendResult.line_error)
        : sendResult.skipped_count > 0 && sendResult.failed_count === 0
          ? t.allSkippedNote.replace('{n}', String(sendResult.skipped_count))
          : sendResult.failed_count > 0
            ? t.allFailedNote.replace('{n}', String(sendResult.failed_count))
            : sendResult.reason || t.sendFailedGeneric
      : null

  const handleSend = useCallback(() => {
    setConfirmOpen(false)
    const coupon = couponCode.trim() || undefined
    if (msgType === 'rich') {
      send({
        lineUserIds: Array.from(selected),
        messageType: 'rich',
        media: {
          richImageFile: rich.imageFile ?? undefined,
          richLink: rich.link.trim(),
          richLabel: rich.label.trim() || undefined,
          richTitle: rich.title.trim() || undefined,
        },
        couponCode: coupon,
      })
    } else if (msgType === 'video') {
      send({
        lineUserIds: Array.from(selected),
        messageType: 'video',
        media: {
          videoFile: video.videoFile ?? undefined,
          previewFile: video.posterFile ?? undefined,
        },
        couponCode: coupon,
      })
    } else {
      send({
        lineUserIds: Array.from(selected),
        textContent: message.trim(),
        includePaymentLink: includeLink,
        messageType: 'text',
        couponCode: coupon,
      })
    }
  }, [send, selected, message, includeLink, msgType, rich, video, couponCode])

  // Revoke rich/video object URLs on unmount to avoid leaks.
  useEffect(() => {
    return () => {
      if (rich.imageUrl) URL.revokeObjectURL(rich.imageUrl)
      if (video.posterUrl) URL.revokeObjectURL(video.posterUrl)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // --- Loading / not-connected gates ---
  if (statusLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[300px]">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!status?.connected) {
    return (
      <div className="max-w-4xl mx-auto p-6 space-y-4">
        <Link to="/line-oa" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" /> {t.back}
        </Link>
        <Card className="rounded-[8px] border border-black/[0.1] max-w-lg">
          <CardContent className="p-6 space-y-3 text-center">
            <h2 className="text-lg font-semibold">{t.connectTitle}</h2>
            <p className="text-sm text-muted-foreground">
              {t.connectDesc}
            </p>
            <Link to="/storefront/settings/line">
              <Button className="bg-[#06C755] hover:bg-[#06C755]/80">{t.connectCta}</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="space-y-1">
        <Link to="/line-oa" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" /> {t.back}
        </Link>
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-[1.6rem] font-semibold text-[#353a40]">{t.title}</h1>
          <LineOAStoreSwitcher />
        </div>
        <p className="text-sm text-muted-foreground">
          {t.subtitle}
        </p>
      </div>

      {/* Step 1 — Choose audience */}
      <Card className="rounded-[8px] border border-black/[0.1]">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-[#06C755] text-white text-xs flex items-center justify-center">1</span>
            <span className="font-medium">{t.step1}</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {SEGMENTS.map(({ key, icon: Icon }) => {
              const active = segment === key
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleSelectSegment(key)}
                  className={`text-left rounded-xl border p-4 transition-colors ${
                    active
                      ? 'border-[#06C755] bg-[#06C755]/[0.05] ring-1 ring-[#06C755]'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${active ? 'bg-[#06C755] text-white' : 'bg-gray-100 text-gray-600'}`}>
                      <Icon className="w-4.5 h-4.5" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[#111]">{t[`seg_${key}`]}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{t[`seg_${key}_desc`]}</p>
                    </div>
                  </div>
                </button>
              )
            })}
          </div>

          {/* Optional numeric filters */}
          <div className="grid grid-cols-2 gap-3 pt-1">
            <div className="space-y-1.5">
              <Label htmlFor="min-orders" className="text-sm">{t.minOrders}</Label>
              <Input
                id="min-orders"
                inputMode="numeric"
                placeholder={t.any}
                value={minOrders}
                onChange={(e) => setMinOrders(e.target.value.replace(/[^0-9]/g, ''))}
                className="rounded"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="min-spend" className="text-sm">{t.minSpend}</Label>
              <Input
                id="min-spend"
                inputMode="numeric"
                placeholder={t.any}
                value={minSpend}
                onChange={(e) => setMinSpend(e.target.value.replace(/[^0-9.]/g, ''))}
                className="rounded"
              />
            </div>
          </div>

          {/* Advanced: refine by purchase history (optional) */}
          <div className="space-y-2 pt-1">
            <Label className="text-sm">{t.refineLabel} <span className="text-muted-foreground font-normal">{t.optional}</span></Label>
            <AudienceFilterBuilder
              conditions={conditions}
              onChange={setConditions}
              options={filterOptions}
              optionsLoading={filterOptionsLoading}
            />
          </div>

          <div className="flex items-center gap-3">
            <Button onClick={handlePreview} disabled={!segment || isPreviewing} className="min-w-[140px] bg-[#06C755] hover:bg-[#06C755]/80">
              {isPreviewing ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-1.5" /> {t.loading}</>
              ) : (
                t.preview
              )}
            </Button>
            {previewData && (
              <span className="text-sm text-muted-foreground">
                <strong className="text-foreground">{previewData.reachable}</strong> {t.reachable}
                {previewData.total_matched > previewData.reachable && (
                  <> · {previewData.total_matched - previewData.reachable} {t.withoutLine}</>
                )}
              </span>
            )}
          </div>

          {previewError && (
            <p className="text-sm text-red-600 flex items-center gap-1.5">
              <AlertCircle className="w-4 h-4" /> {previewError}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Step 2 — Recipients + message (after preview) */}
      {previewData && (
        <>
          <Card className="rounded-[8px] border border-black/[0.1]">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-[#06C755] text-white text-xs flex items-center justify-center">2</span>
                  <span className="font-medium">{t.step2}</span>
                </div>
                <Badge className="bg-gray-100 text-gray-700 border-0">
                  {t.selectedCount.replace('{a}', String(selected.size)).replace('{b}', String(recipients.length))}
                </Badge>
              </div>

              {recipients.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  {t.noMatch}
                </p>
              ) : (
                <div className="border border-gray-100 rounded-lg overflow-hidden">
                  <div className="flex items-center gap-3 px-4 py-2.5 bg-gray-50 border-b border-gray-100">
                    <Checkbox checked={allSelected} onCheckedChange={toggleAll} />
                    <span className="text-xs font-medium text-gray-600 flex-1">{t.colCustomer}</span>
                    <span className="text-xs font-medium text-gray-600 w-16 text-right">{t.colOrders}</span>
                    <span className="text-xs font-medium text-gray-600 w-24 text-right">{t.colSpend}</span>
                  </div>
                  <ScrollArea className="max-h-[340px]">
                    {recipients.map((r) => (
                      <label
                        key={r.line_user_id}
                        className="flex items-center gap-3 px-4 py-2.5 border-b border-gray-50 last:border-0 hover:bg-gray-50 cursor-pointer"
                      >
                        <Checkbox
                          checked={selected.has(r.line_user_id)}
                          onCheckedChange={() => toggleOne(r.line_user_id)}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-[#111] truncate">{r.customer_name}</p>
                          {r.unpaid_count > 0 && (
                            <span className="text-[11px] text-amber-600">{t.unpaidCount.replace('{n}', String(r.unpaid_count))}</span>
                          )}
                          {/* Cart segments: a never-purchased shopper reads as a phantom "0" in the
                              Orders column. Surface a clear "new customer" chip instead so it's an
                              actionable signal, not broken-looking data. */}
                          {cartSegment && r.order_count === 0 && (
                            <span className="ml-1 inline-flex items-center rounded-full bg-emerald-50 text-emerald-600 text-[11px] px-2 py-0.5">
                              {t.newCustomer}
                            </span>
                          )}
                          {r.items && r.items.length > 0 && (
                            <p className="text-[11px] text-gray-500 truncate mt-0.5">
                              <span className="text-gray-400">
                                {segment === 'awaiting_payment'
                                  ? t.inOrderCount.replace('{n}', String(r.item_count ?? r.items.length))
                                  : t.inCartCount.replace('{n}', String(r.item_count ?? r.items.length))}
                              </span>{' '}
                              {r.items.slice(0, 3).map((it) => `${it.name} ×${it.qty}`).join('  ·  ')}
                              {r.items.length > 3 && `  ${t.moreItems.replace('{n}', String(r.items.length - 3))}`}
                            </p>
                          )}
                        </div>
                        <span className="text-sm text-gray-600 w-16 text-right">
                          {cartSegment && r.order_count === 0 ? (
                            <span className="text-gray-300">{t.noOrdersHint}</span>
                          ) : (
                            r.order_count
                          )}
                        </span>
                        <span className="text-sm text-gray-900 w-24 text-right">{baht(r.total_spend)}</span>
                      </label>
                    ))}
                  </ScrollArea>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Step 3 — Compose */}
          <Card className="rounded-[8px] border border-black/[0.1]">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-[#06C755] text-white text-xs flex items-center justify-center">3</span>
                <span className="font-medium">{t.step3}</span>
              </div>

              {/* Message-type selector — same three modes as the broadcast compose. */}
              <div className="inline-flex flex-wrap rounded-lg border border-black/10 bg-gray-100 p-1 gap-1">
                {([
                  { id: 'text', label: t.typeText, Icon: Type },
                  { id: 'rich', label: t.typeRich, Icon: Sparkles },
                  { id: 'video', label: t.typeVideo, Icon: Film },
                ] as { id: CampaignMessageType; label: string; Icon: typeof Type }[]).map(({ id, label, Icon }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setMsgType(id)}
                    className={`inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                      msgType === id
                        ? 'bg-white text-[#06C755] shadow-sm'
                        : 'text-black/50 hover:text-[#353a40]'
                    }`}
                  >
                    <Icon className="w-4 h-4" /> {label}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  {msgType === 'text' && (
                    <>
                      <Textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value.slice(0, MAX_TEXT))}
                        placeholder={t.messagePlaceholder}
                        className="rounded min-h-[160px] resize-none"
                      />
                      <p className="text-xs text-muted-foreground text-right">{message.length}/{MAX_TEXT}</p>

                      {segment === 'awaiting_payment' && (
                        <div className="flex items-start justify-between gap-3 rounded-lg border border-gray-100 p-3">
                          <div className="flex items-start gap-2">
                            <Link2 className="w-4 h-4 mt-0.5 text-gray-500" />
                            <div>
                              <p className="text-sm font-medium">{t.includeLinkTitle}</p>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {t.includeLinkDesc}
                              </p>
                            </div>
                          </div>
                          <Switch checked={includeLink} onCheckedChange={setIncludeLink} />
                        </div>
                      )}
                    </>
                  )}

                  {msgType === 'rich' && (
                    <div className="rounded-[5px] border border-black/[0.15] bg-white overflow-hidden">
                      <div className="border-b border-black/10 bg-[#f8f9fa] px-3 py-2 text-[11px] text-black/50">
                        {t.richHint}
                      </div>
                      <RichEditor rich={rich} setRich={setRich} />
                    </div>
                  )}

                  {msgType === 'video' && (
                    <div className="rounded-[5px] border border-black/[0.15] bg-white overflow-hidden">
                      <div className="border-b border-black/10 bg-[#f8f9fa] px-3 py-2 text-[11px] text-black/50">
                        {t.videoHint}
                      </div>
                      <VideoEditor video={video} setVideo={setVideo} />
                    </div>
                  )}

                  {/* Coupon (optional) — stored on the send for exact order attribution */}
                  <div className="rounded-lg border border-gray-100 p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <Ticket className="w-4 h-4 text-gray-500" />
                      <p className="text-sm font-medium">{t.couponLabel}</p>
                      <span className="text-xs text-muted-foreground">{t.couponOptional}</span>
                    </div>
                    <div className="relative">
                      <select
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value)}
                        className="w-full appearance-none rounded-md border border-gray-200 bg-white px-3 py-2 pr-8 text-sm text-gray-900 focus:border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-100"
                      >
                        <option value="">{t.couponNone}</option>
                        {coupons.map((c) => (
                          <option key={c.coupon_code} value={c.coupon_code}>
                            {c.promotion_title} · {c.coupon_code}
                            {c.discount_display ? ` · ${c.discount_display}` : ''}
                            {c.status !== 'Active' ? ` (${c.status})` : ''}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    </div>
                    <p className="text-xs text-muted-foreground">{t.couponHint}</p>
                    {couponCode && msgType === 'text' && !message.includes(couponCode) && (
                      <button
                        type="button"
                        onClick={() => setMessage((m) => `${m}${m && !m.endsWith('\n') ? '\n' : ''}${t.couponCodePrefix}${couponCode}`.slice(0, MAX_TEXT))}
                        className="text-xs font-medium text-[#06C755] hover:underline"
                      >
                        {t.couponInsert}
                      </button>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">{t.previewLabel}</Label>
                  <MessagePreview
                    mode={msgType}
                    text={message}
                    withLink={includeLink && segment === 'awaiting_payment'}
                    placeholder={t.messagePreviewPlaceholder}
                    rich={{ imageUrl: rich.imageUrl, title: rich.title, label: rich.label }}
                    video={{ posterUrl: video.posterUrl }}
                  />
                </div>
              </div>

              {sendError && (
                <p className="text-sm text-red-600 flex items-center gap-1.5">
                  <AlertCircle className="w-4 h-4" /> {sendError}
                </p>
              )}

              {sendSucceeded ? (
                <div className="rounded-lg bg-green-50 text-green-800 p-4 text-sm">
                  <p className="font-medium flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4" /> {t.sentTo.replace('{n}', String(sendResult!.sent_count))}
                  </p>
                  {(sendResult!.skipped_count > 0 || sendResult!.failed_count > 0) && (
                    <p className="mt-1 text-green-700">
                      {sendResult!.skipped_count > 0 && <>{t.skippedNote.replace('{n}', String(sendResult!.skipped_count))}</>}
                      {sendResult!.failed_count > 0 && <>{t.failedNote.replace('{n}', String(sendResult!.failed_count))}</>}
                    </p>
                  )}
                </div>
              ) : (
                <>
                  {sendFailureMsg && (
                    <div className="rounded-lg bg-red-50 text-red-800 p-4 text-sm">
                      <p className="font-medium flex items-center gap-1.5">
                        <AlertCircle className="w-4 h-4" /> {t.sendFailedTitle}
                      </p>
                      <p className="mt-1 text-red-700 break-words">{sendFailureMsg}</p>
                    </div>
                  )}
                  <div className="flex justify-end">
                    <Button
                      onClick={() => setConfirmOpen(true)}
                      disabled={!canSend}
                      className="min-w-[180px] bg-[#06C755] hover:bg-[#06C755]/80"
                    >
                      {isSending ? (
                        <><Loader2 className="w-4 h-4 animate-spin mr-1.5" /> {t.sending}</>
                      ) : (
                        <><Send className="w-4 h-4 mr-1.5" /> {t.sendTo.replace('{n}', String(selected.size))}</>
                      )}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Sent history — past targeted campaign sends (Delivered + status only) */}
      <CampaignSentHistory enabled={!!status?.connected} />

      {/* Confirm dialog */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t.confirmTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              {t.confirmBody
                .replace('{n}', String(selected.size))
                .replace('{segment}', segment ? t[`seg_${segment}`] : '')}
              {includeLink && segment === 'awaiting_payment' && t.eachGetsLink}
              {t.confirmUndo}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t.cancel}</AlertDialogCancel>
            <AlertDialogAction onClick={handleSend} className="bg-[#06C755] hover:bg-[#06C755]/80">{t.sendNow}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
